"use client";

import { useRef, useState } from "react";
import {
  SelectField,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import { CARD } from "@/components/tool-kit/styles";
import {
  PaperControls,
  PRINTABLES_COMMON_STRINGS,
  PrintablePages,
  PrintBar,
  useTextMeasure,
} from "@/components/tools/printables/page-kit";
import { PlannerPageArt } from "@/components/tools/study-more/planner-page-art";
import { useToolUi } from "@/components/tools/tool-ui";
import {
  type PaperSize,
  pageGeometry,
  parseLines,
} from "@/lib/tool-kit/printables/page";
import { layoutSq3r, sq3rRows } from "@/lib/tool-kit/study-more/sq3r-sheet";

const ACCENT = "#1f4e79";
const ROW_CHOICES = ["4", "5", "6", "8", "10", "12"] as const;

/** English strings. A translated content file overrides these through `ui`. */
export const SQ3R_READING_WORKSHEET_STRINGS = {
  ...PRINTABLES_COMMON_STRINGS,
  nameLabel: "Name",
  courseLabel: "Class",
  readingLabel: "Reading",
  pagesLabel: "Pages",
  dateLabel: "Date",
  headerHint:
    "Typed text is printed on the sheet; leave a field empty to write it by hand.",
  headingsLabel: "Headings from the chapter — one per line (optional)",
  headingsPlaceholder: "Causes of the war\nKey battles\nConsequences",
  headingsHint:
    "Up to twelve. Each heading gets its own row for a question and for an answer from memory. Leave this empty for blank rows.",
  rowsLabel: "Blank question rows",
  sheetTitle: "SQ3R Reading Worksheet",
  surveyTitle: "Survey",
  surveyHint: "Skim before you read: get the shape of the chapter.",
  surveyChecks: [
    "Title and introduction",
    "Headings and subheadings",
    "Bold or italic terms",
    "Figures, tables and captions",
    "Chapter summary",
    "End-of-chapter questions",
  ],
  surveyTopic: "I think this chapter is about:",
  questionTitle: "Question",
  questionHint: "Turn each heading into a question you want answered.",
  questionHeading: "Heading",
  questionMine: "My question",
  readTitle: "Read",
  readHint: "One section at a time, reading to answer your question.",
  reciteTitle: "Recite",
  reciteHint: "Look away and answer aloud, then write it in your own words.",
  reciteAnswer: "Answer from memory",
  reciteCheck: "Checked",
  reviewTitle: "Review",
  reviewHint: "Go back over it: what stuck, and what did not?",
  reviewSummary: "Summary in my own words:",
  reviewUnclear: "Still unclear:",
  reviewAgain: "Review again:",
  reviewDates: ["tomorrow", "in a week", "before the exam"],
  previewLabel: "SQ3R worksheet preview",
  fileName: "sq3r-worksheet",
} as const;

export default function Sq3rReadingWorksheetWidget() {
  const strings = useToolUi(SQ3R_READING_WORKSHEET_STRINGS);
  const measure = useTextMeasure();
  const svgRef = useRef<SVGSVGElement>(null);
  const [name, setName] = useState("");
  const [course, setCourse] = useState("");
  const [reading, setReading] = useState("");
  const [pages, setPages] = useState("");
  const [date, setDate] = useState("");
  const [headings, setHeadings] = useState("");
  const [blankRows, setBlankRows] = useState<string>("6");
  const [paper, setPaper] = useState<PaperSize>("letter");

  const geometry = pageGeometry(paper, "portrait");
  const rows = sq3rRows(parseLines(headings), Number(blankRows));
  const [first, second] = layoutSq3r(geometry, measure, {
    header: { name, course, reading, pages, date },
    headings: rows,
    labels: {
      title: strings.sheetTitle,
      name: strings.nameLabel,
      course: strings.courseLabel,
      reading: strings.readingLabel,
      pages: strings.pagesLabel,
      date: strings.dateLabel,
      survey: {
        title: strings.surveyTitle,
        hint: strings.surveyHint,
        checks: strings.surveyChecks,
        topic: strings.surveyTopic,
      },
      question: {
        title: strings.questionTitle,
        hint: strings.questionHint,
        heading: strings.questionHeading,
        mine: strings.questionMine,
      },
      read: { title: strings.readTitle, hint: strings.readHint },
      recite: {
        title: strings.reciteTitle,
        hint: strings.reciteHint,
        answer: strings.reciteAnswer,
        check: strings.reciteCheck,
      },
      review: {
        title: strings.reviewTitle,
        hint: strings.reviewHint,
        summary: strings.reviewSummary,
        unclear: strings.reviewUnclear,
        again: strings.reviewAgain,
        dates: strings.reviewDates,
      },
    },
  });

  return (
    <div className="space-y-6 print:m-0 print:space-y-0">
      <div className="space-y-5 print:hidden">
        <div className={`${CARD} grid gap-4 sm:grid-cols-2 lg:grid-cols-3`}>
          <TextField
            id="sq-name"
            label={strings.nameLabel}
            onChange={setName}
            value={name}
          />
          <TextField
            id="sq-course"
            label={strings.courseLabel}
            onChange={setCourse}
            value={course}
          />
          <TextField
            id="sq-date"
            label={strings.dateLabel}
            onChange={setDate}
            value={date}
          />
          <TextField
            id="sq-reading"
            label={strings.readingLabel}
            onChange={setReading}
            value={reading}
          />
          <TextField
            id="sq-pages"
            label={strings.pagesLabel}
            onChange={setPages}
            value={pages}
          />
          <SelectField
            id="sq-rows"
            label={strings.rowsLabel}
            onChange={setBlankRows}
            options={ROW_CHOICES.map((value) => ({ value, label: value }))}
            value={blankRows}
          />
          <PaperControls
            idPrefix="sq"
            onPaper={setPaper}
            paper={paper}
            strings={strings}
          />
        </div>
        <TextAreaField
          hint={strings.headingsHint}
          id="sq-headings"
          label={strings.headingsLabel}
          onChange={setHeadings}
          placeholder={strings.headingsPlaceholder}
          prose
          rows={4}
          value={headings}
        />
        <PrintBar
          fileName={`${strings.fileName}-${paper}`}
          pageTotal={2}
          paper={paper}
          strings={strings}
          svgRef={svgRef}
        />
      </div>
      <PrintablePages
        className="max-w-xl"
        geometry={geometry}
        label={strings.previewLabel}
        pages={[
          <PlannerPageArt accent={ACCENT} key="one" page={first} />,
          <PlannerPageArt
            accent={ACCENT}
            accentFirst={false}
            key="two"
            page={second}
          />,
        ]}
        svgRef={svgRef}
      />
    </div>
  );
}
